import { dayjs } from '@convenia/utils'
import { MULTI_SELECT_SEPARATOR, is } from '@convenia/helpers'
import {
  SYSTEM_FIELD_TYPE_TEXT,
  SYSTEM_FIELD_TYPE_LIST,
  SYSTEM_FIELD_TYPE_MULTIPLE,
  SYSTEM_FIELD_TYPE_BOOLEAN,
  SYSTEM_FIELD_TYPE_DATE,
  SYSTEM_FIELD_TYPE_FILE,
  SYSTEM_FIELD_FORM_BUILDER_TYPE_TEXT,
  SYSTEM_FIELD_FORM_BUILDER_TYPE_SELECT,
  SYSTEM_FIELD_FORM_BUILDER_TYPE_MULTI_SELECT,
  SYSTEM_FIELD_FORM_BUILDER_TYPE_RADIO,
  SYSTEM_FIELD_FORM_BUILDER_TYPE_DATE,
  SYSTEM_FIELD_FORM_BUILDER_TYPE_FILE,
} from '@convenia/common-organisms/SystemFields/content/consts/FieldTypes'

const SYSTEM_FIELD_TYPE_RADIO_OPTION_YES = 'Sim'
const SYSTEM_FIELD_TYPE_RADIO_OPTION_NO = 'Não'

const DEFAULT_TRACK_BY = 'value'
const DEFAULT_DISPLAY_BY = 'label'

const BOOLEAN_OPTIONS = [
  { label: SYSTEM_FIELD_TYPE_RADIO_OPTION_YES, value: SYSTEM_FIELD_TYPE_RADIO_OPTION_YES },
  { label: SYSTEM_FIELD_TYPE_RADIO_OPTION_NO, value: SYSTEM_FIELD_TYPE_RADIO_OPTION_NO }
]

const SYSTEM_FIELD_SCHEMA = {
  [SYSTEM_FIELD_TYPE_TEXT]: {
    type: SYSTEM_FIELD_FORM_BUILDER_TYPE_TEXT,
    value: '',
    rows: 3,
    textArea: true,
    maxLength: 300
  },
  [SYSTEM_FIELD_TYPE_LIST]: {
    type: SYSTEM_FIELD_FORM_BUILDER_TYPE_SELECT,
    value: '',
    options: [],
    trackBy: DEFAULT_TRACK_BY,
    displayBy: DEFAULT_DISPLAY_BY,
    nullable: false
  },
  [SYSTEM_FIELD_TYPE_MULTIPLE]: {
    type: SYSTEM_FIELD_FORM_BUILDER_TYPE_MULTI_SELECT,
    value: [],
    options: [],
    trackBy: DEFAULT_TRACK_BY,
    displayBy: DEFAULT_DISPLAY_BY,
    nullable: false
  },
  [SYSTEM_FIELD_TYPE_BOOLEAN]: {
    type: SYSTEM_FIELD_FORM_BUILDER_TYPE_RADIO,
    value: '',
    options: BOOLEAN_OPTIONS,
    trackBy: DEFAULT_TRACK_BY,
    displayBy: DEFAULT_DISPLAY_BY
  },
  [SYSTEM_FIELD_TYPE_DATE]: {
    type: SYSTEM_FIELD_FORM_BUILDER_TYPE_DATE,
    value: ''
  },
  [SYSTEM_FIELD_TYPE_FILE]: {
    type: SYSTEM_FIELD_FORM_BUILDER_TYPE_FILE,
    value: ''
  },
}

/**
 * Converts a backend multi-select value into the array expected by CFormBuilder.
 * Handles arrays, comma-separated strings, objects, and primitives.
 * @param {Array|string|Object|*} value - The value to normalize
 * @returns {Array<string>} Array of normalized string values
 * @example
 * normalizeMultiSelectValue('opt1;opt2') // ['opt1', 'opt2']
 * normalizeMultiSelectValue([{ value: 'a' }, 'b']) // ['a', 'b']
 */
const normalizeMultiSelectValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map(item => {
        if (is(item, 'Object')) return item.value ?? item.label ?? ''

        return String(item).trim()
      })
      .filter(Boolean)
  }

  if (typeof value === 'string') {
    return value
      .split(MULTI_SELECT_SEPARATOR)
      .map(item => item.trim())
      .filter(Boolean)
  }

  if (value == null) return []

  if (is(value, 'Object')) {
    return [ value.value ?? value.label ?? '' ].filter(Boolean)
  }

  return [ String(value).trim() ]
}

/**
 * Resolves the appropriate field value based on its type.
 * Multi-select types return arrays; others return scalar values.
 * @param {string} type - The field type from SYSTEM_FIELD_TYPE_*
 * @param {*} value - The primary value to resolve
 * @param {*} fallbackValue - The fallback value if primary is not provided
 * @returns {Array|*} Normalized field value
 */
const resolveFieldValue = (type, value, fallbackValue) => {
  if ([ SYSTEM_FIELD_TYPE_MULTIPLE, SYSTEM_FIELD_FORM_BUILDER_TYPE_MULTI_SELECT ].includes(type)) {
    return normalizeMultiSelectValue(value ?? fallbackValue)
  }

  if (type === SYSTEM_FIELD_TYPE_DATE) {
    return value && !Array.isArray(value) ? dayjs(value, 'DD/MM/YYYY').toDate() : fallbackValue
  }

  if (Array.isArray(value)) return value[0] ?? fallbackValue

  return value ?? fallbackValue
}

export const resolveCustomFieldValues = (items = [], { schema, values } = {}) =>
  items.map(item => ({
    ...item,
    ...Object.entries(values?.[item.id] || {})
      .reduce((acc, [ id, value ]) => ({
        ...acc,
        [id]: resolveFieldValue(schema?.[id]?.type, value, schema?.[id]?.value)
      }), {})
  }))

/**
 * Normalizes backend option values to CFormBuilder option objects.
 * Ensures all options have 'label' and 'value' properties.
 * @param {string} type - The field type to determine special handling
 * @param {Array<Object|string>} [options=[]] - Raw options from backend
 * @returns {Array<{label: string, value: string}>} Normalized options array
 * @example
 * buildFieldOptions('text', ['Option 1', 'Option 2'])
 * // [{label: 'Option 1', value: 'Option 1'}, {label: 'Option 2', value: 'Option 2'}]
 */
const buildFieldOptions = (type, options = []) => {
  if (!Array.isArray(options)) {
    return type === SYSTEM_FIELD_TYPE_BOOLEAN ? BOOLEAN_OPTIONS : []
  }

  if (type === SYSTEM_FIELD_TYPE_BOOLEAN && !options.length) {
    return BOOLEAN_OPTIONS
  }

  return options.map(option => {
    if (is(option, 'Object')) {
      return {
        ...option,
        label: option.label ?? option.value ?? '',
        value: option.value ?? option.label ?? ''
      }
    }

    return {
      label: option,
      value: option
    }
  })
}

/**
 * Returns a supported form builder type for a field.
 * Falls back to text type if the field type is not supported.
 * @param {Object} field - The field object with a 'type' property
 * @param {string} [field.type] - The field type identifier
 * @returns {string} A type from SYSTEM_FIELD_SCHEMA or SYSTEM_FIELD_FORM_BUILDER_TYPE_TEXT
 */
const getFieldType = field => (
  field.type && SYSTEM_FIELD_SCHEMA[field.type]
    ? field.type
    : SYSTEM_FIELD_FORM_BUILDER_TYPE_TEXT
)

/**
 * Converts the backend custom field schema to CFormBuilder-compatible schema.
 * Merges field definitions with values, applying type-specific normalization.
 * The field value comes from `values` prop first, falling back to `schema[field].value`.
 * @param {Object} [schema={}] - Backend field schema object keyed by field ID
 * @param {Object} [schema[fieldId]] - Individual field definition
 * @param {string} [schema[fieldId].label] - Field label
 * @param {*} [schema[fieldId].value] - Default field value
 * @param {boolean} [schema[fieldId].mandatory] - Whether field is required
 * @param {Array} [schema[fieldId].options] - Field options for selects
 * @param {Object} [values={}] - Field values keyed by field ID, overrides schema defaults
 * @returns {Object} CFormBuilder schema object ready for use in forms
 */
export const createFormBuilderSchema = (schema = {}, area) => {
  return Object.keys(schema).reduce((acc, fieldId) => {
    const field = schema[fieldId] || {}
    const type = getFieldType(field)
    const baseSchema = SYSTEM_FIELD_SCHEMA[type] || SYSTEM_FIELD_SCHEMA[SYSTEM_FIELD_TYPE_TEXT]
    const required = Boolean(field?.mandatory)
    const value = resolveFieldValue(type, field?.value, baseSchema.value)
    const disabled = area && !field.editableFor?.includes(area)

    acc[fieldId] = {
      ...baseSchema,
      id: field.id || fieldId,
      label: field.label || '',
      placeholder: field.label || '',
      value,
      disabled,
      options: buildFieldOptions(type, field.options),
      required,
      nullable: [ 'list', 'multiple' ].includes(field.type) && !required,
      relationships: field.relationships ?? field.relationshipIds ?? [],
      ...(required ? { validation: 'required' } : {})
    }

    return acc
  }, {})
}

/**
 * Adapts employee custom field sections to the shape consumed by the SPA.
 * Transforms each section by converting its schema and preserving metadata.
 * @param {Array<Object>} [fields=[]] - Array of custom field section objects
 * @param {Object} [fields[n].schema] - Backend schema for this section
 * @param {Object} [fields[n].values] - Values for fields in this section
 * @returns {Array<Object>} Transformed field sections with CFormBuilder-compatible schemas
 */
export const mapEmployeeCustomFields = (fields = [], area) =>
  fields.map(({ schema = {}, values = {}, ...field }) => ({
    ...field,
    schema: createFormBuilderSchema(schema, area),
    values
  }))
